import { Navigate, Route, Routes, useNavigate } from "react-router";
import { useEffect } from "react";
import { TenantProvider, useTenant } from "@/lib/tenant-context.js";
import { getActiveTenantSlug, useSession } from "@/lib/session.js";
import { LoginPage } from "@/routes/login.js";
import { SetupPage } from "@/routes/setup.js";
import { TenantShell } from "@/routes/shell.js";
import { CalendarPage } from "@/routes/calendar.js";
import { DispatchPage } from "@/routes/dispatch.js";
import { DayListPage } from "@/routes/day-list.js";
import { MePage } from "@/routes/me.js";
import { CustomersPage } from "@/routes/customers.js";
import { ServicesPage } from "@/routes/services.js";
import { ResourcesPage } from "@/routes/resources.js";
import { ResourceSkillsPage } from "@/routes/resource-skills.js";
import { SchedulesPage } from "@/routes/schedules.js";
import { RecurrencesPage } from "@/routes/recurrences.js";
import { ClosuresPage } from "@/routes/closures.js";
import { SyncConflictsPage } from "@/routes/sync-conflicts.js";
import { CalendarSyncPage } from "@/routes/calendar-sync.js";
import { WebhooksPage } from "@/routes/webhooks.js";
import { AuditLogPage } from "@/routes/audit-log.js";
import { DlqPage } from "@/routes/dlq.js";
import { SettingsPage } from "@/routes/settings.js";

export function App(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/*" element={<SessionBoundary />} />
    </Routes>
  );
}

/**
 * Tenant identity comes from the cookie session set by /api/v1/auth/sso/callback.
 * SessionProvider has already redirected to /api/v1/auth/sso/start when /auth/me
 * returned 401, so by the time we render here we either have session.data or are
 * still loading. No tenant slug in the URL — URLs are clean
 * (host.com/calendar/dispatch instead of host.com/calendar/t/<slug>/dispatch).
 */
function SessionBoundary(): React.JSX.Element {
  const session = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (session.error) {
      // Boot-time auth/me failure that wasn't a 401 (which would have already
      // redirected). Most likely a network blip; surface a retry affordance.
    }
  }, [session.error, navigate]);

  if (session.loading) {
    return <div className="flex h-dvh items-center justify-center text-sm text-muted-foreground">Loading session…</div>;
  }
  if (!session.data) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-2 p-6 text-center">
        <h1 className="text-xl font-semibold">Session unavailable</h1>
        <p className="text-sm text-muted-foreground">{session.error?.message ?? "Unknown error"}</p>
      </div>
    );
  }

  const principal = session.data.principal;
  const actorId = principal.kind === "tenant_user" ? principal.userId
    : principal.kind === "superadmin" ? principal.platformUserId
    : "";

  // Superadmins (UMS Console admins) can switch tenants; the chosen slug is
  // persisted in localStorage and only honored when it's a tenant they were
  // actually offered (session.data.tenants). Everyone else — and any stale or
  // unknown stored slug — falls back to the cookie's own tenant.
  const defaultSlug = session.data.tenant.tenantSlug;
  const stored = getActiveTenantSlug();
  const tenantSlug =
    principal.kind === "superadmin" && stored && (session.data.tenants ?? []).some((t) => t.slug === stored)
      ? stored
      : defaultSlug;

  return (
    <TenantProvider tenantSlug={tenantSlug} actorId={actorId}>
      <TenantRoutes />
    </TenantProvider>
  );
}

function TenantRoutes(): React.JSX.Element {
  const tenant = useTenant();

  if (tenant.loading) {
    return (
      <div className="flex h-dvh items-center justify-center text-sm text-muted-foreground">
        Loading tenant…
      </div>
    );
  }

  if (tenant.error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-2 p-6 text-center">
        <h1 className="text-xl font-semibold">Could not load tenant</h1>
        <p className="text-sm text-muted-foreground">{tenant.error.message}</p>
        <button
          className="text-sm underline"
          onClick={() => tenant.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <TenantShell>
      <Routes>
        <Route index element={<RouteToDefaultView />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="dispatch" element={<DispatchPage />} />
        <Route path="day-list" element={<DayListPage />} />
        <Route path="me" element={<MePage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="resource-skills" element={<ResourceSkillsPage />} />
        <Route path="schedules" element={<SchedulesPage />} />
        <Route path="recurrences" element={<RecurrencesPage />} />
        <Route path="closures" element={<ClosuresPage />} />
        <Route path="sync-conflicts" element={<SyncConflictsPage />} />
        <Route path="calendar-sync" element={<CalendarSyncPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="dlq" element={<DlqPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </TenantShell>
  );
}

/**
 * Render the tenant's default view directly at the mount root so the URL
 * stays at /calendar/ instead of redirecting to /calendar/calendar.
 * Bookmarkable explicit views (/calendar, /dispatch, /day-list) still
 * exist for users who want a stable URL regardless of default-view changes.
 */
function RouteToDefaultView(): React.JSX.Element {
  const tenant = useTenant();
  const view = tenant.profile?.default_view ?? "grid";
  if (view === "grid") return <CalendarPage />;
  if (view === "dispatch") return <DispatchPage />;
  return <DayListPage />;
}
